import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { 
  LayoutDashboard, 
  ListTodo, 
  Columns3, 
  ChevronLeft,
  ChevronRight,
  Terminal,
  X,
  KeyRound,
  Loader2,
  Menu
} from "lucide-react";
import CustomUserMenu from "./CustomUserMenu";
import CustomNotifications from "./CustomNotifications";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV_ITEMS = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/tasks", label: "Lista Task", icon: ListTodo },
  { path: "/kanban", label: "Kanban", icon: Columns3 },
];

export default function AppShell({ children, user, onLogout }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const location = useLocation();
  
  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    fetchDeadlineNotifications();
    const interval = setInterval(fetchDeadlineNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchDeadlineNotifications = async () => {
    try {
      const response = await axios.get(`${API}/tasks`);
      const tasks = response.data;
      const today = new Date();
      
      const upcomingDeadlines = tasks
        .filter(task => {
          if (!task.deadline || task.status === "ONLINE") return false;
          const deadline = new Date(task.deadline);
          const daysLeft = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
          return daysLeft <= 7 && daysLeft >= -3;
        })
        .map(task => {
          const deadline = new Date(task.deadline);
          const daysLeft = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
          return {
            ...task,
            daysLeft,
            isOverdue: daysLeft < 0,
            urgency: daysLeft < 0 ? 'overdue' : daysLeft <= 2 ? 'critical' : 'warning'
          };
        })
        .sort((a, b) => a.daysLeft - b.daysLeft);
      
      setNotifications(upcomingDeadlines);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const dismissNotification = (taskId) => {
    const dismissed = JSON.parse(localStorage.getItem('dismissedNotifs') || '[]');
    dismissed.push(taskId);
    localStorage.setItem('dismissedNotifs', JSON.stringify(dismissed));
    setNotifications(prev => prev.filter(n => n.id !== taskId));
  };

  const filteredNotifications = notifications.filter(n => {
    const dismissed = JSON.parse(localStorage.getItem('dismissedNotifs') || '[]');
    return !dismissed.includes(n.id);
  });

  const handleChangePassword = async () => {
    if (!currentPassword) {
      toast.error("Inserisci la password attuale");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("La nuova password deve avere almeno 6 caratteri");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }

    setChangingPassword(true);
    try {
      const response = await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      if (response.data.success) {
        toast.success("Password cambiata con successo!");
        setShowChangePassword(false);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel cambio password");
    } finally {
      setChangingPassword(false);
    }
  };

  // Navigation content (shared between desktop and mobile)
  const NavContent = ({ isMobile = false }) => (
    <nav className={`flex-1 p-3 space-y-1 ${isMobile ? 'pt-2' : ''}`}>
      {NAV_ITEMS.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;

        return (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={() => isMobile && setMobileMenuOpen(false)}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
              isActive 
                ? 'bg-secondary text-foreground' 
                : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
            }`}
            data-testid={isMobile ? `mobile-nav-${item.path.replace('/', '')}` : `nav-${item.path.replace('/', '')}`}
          >
            <Icon className="w-5 h-5 flex-shrink-0" />
            <span>{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside 
        className={`hidden md:flex fixed left-0 top-0 h-full bg-background border-r border-border z-50 flex-col transition-all duration-300 ${
          collapsed ? 'w-16' : 'w-64'
        }`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-border">
          <div className="flex items-center gap-2 text-primary overflow-hidden">
            <Terminal className="w-6 h-6 flex-shrink-0" />
            {!collapsed && (
              <span className="font-bold tracking-tight whitespace-nowrap">TASK_CMD //</span>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive 
                    ? 'bg-secondary text-foreground' 
                    : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                }`}
                data-testid={`nav-${item.path.replace('/', '')}`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && <span>{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>

        {/* Collapse toggle */}
        <div className="p-3 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCollapsed(!collapsed)}
            className="w-full justify-center"
            data-testid="collapse-btn"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className={`flex-1 transition-all duration-300 ${collapsed ? 'md:ml-16' : 'md:ml-64'}`}>
        {/* Header */}
        <header className="h-14 md:h-16 border-b border-border flex items-center justify-between px-4 md:px-6 bg-background sticky top-0 z-40">
          {/* Mobile menu button */}
          <div className="flex items-center gap-3 md:hidden">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2 text-primary">
              <Terminal className="w-5 h-5" />
              <span className="font-bold text-sm tracking-tight">TASK_CMD</span>
            </div>
          </div>

          {/* Desktop spacer */}
          <div className="hidden md:block" />

          {/* Right side actions */}
          <div className="flex items-center gap-2">
            {/* Notifications - Custom component */}
            <CustomNotifications
              notifications={filteredNotifications}
              onDismiss={dismissNotification}
              onDismissAll={() => {
                localStorage.setItem('dismissedNotifs', JSON.stringify(notifications.map(n => n.id)));
                setNotifications([]);
              }}
            />

            {/* User menu - Custom component */}
            <CustomUserMenu
              user={user}
              onChangePassword={() => setShowChangePassword(true)}
              onLogout={onLogout}
            />
          </div>
        </header>

        {/* Page content */}
        <div className="min-h-[calc(100vh-3.5rem)] md:min-h-[calc(100vh-4rem)] pb-20 md:pb-12">
          {children}
        </div>

        {/* TelcaVoIP Badge */}
        <footer className="fixed bottom-0 right-0 p-3 md:p-4 z-50">
          <a 
            href="https://telcavoip.ai" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 md:gap-2 text-[10px] md:text-xs text-muted-foreground hover:text-primary transition-colors bg-card/80 backdrop-blur-sm px-2 md:px-3 py-1.5 md:py-2 rounded-lg border border-border"
          >
            <span>Made With</span>
            <svg className="w-2.5 h-2.5 md:w-3 md:h-3 text-red-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
            </svg>
            <span>By <strong className="text-primary">TelcaVoIP AI</strong></span>
          </a>
        </footer>
      </main>

      {/* Mobile Menu Slide-in */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/80"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Menu Panel */}
          <div className="fixed inset-y-0 left-0 w-64 bg-card border-r border-border animate-in slide-in-from-left duration-300">
            <div className="h-14 flex items-center justify-between px-4 border-b border-border">
              <div className="flex items-center gap-2 text-primary">
                <Terminal className="w-5 h-5" />
                <span className="font-bold tracking-tight">TASK_CMD //</span>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setMobileMenuOpen(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <NavContent isMobile={true} />
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 z-50">
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/80"
            onClick={() => setShowChangePassword(false)}
          />
          
          {/* Content */}
          <div className="fixed left-[50%] top-[50%] z-50 w-[calc(100%-2rem)] sm:max-w-[400px] translate-x-[-50%] translate-y-[-50%] bg-card border border-border p-6 shadow-lg rounded-lg" data-testid="change-password-dialog">
            {/* Close button */}
            <button
              type="button"
              onClick={() => setShowChangePassword(false)}
              className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 focus:outline-none"
            >
              <X className="h-4 w-4" />
            </button>
            
            <div className="flex flex-col space-y-1.5 text-center sm:text-left mb-4">
              <h2 className="text-lg font-semibold leading-none tracking-tight flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-primary" />
                Cambia Password
              </h2>
              <p className="text-sm text-muted-foreground">
                Inserisci la password attuale e quella nuova
              </p>
            </div>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Password Attuale</Label>
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-secondary"
                  data-testid="current-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Nuova Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimo 6 caratteri"
                  className="bg-secondary"
                  data-testid="new-password-change-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Conferma Nuova Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Ripeti la password"
                  className="bg-secondary"
                  data-testid="confirm-password-change-input"
                />
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowChangePassword(false)} className="w-full sm:w-auto">
                Annulla
              </Button>
              <Button onClick={handleChangePassword} disabled={changingPassword} data-testid="save-password-btn" className="w-full sm:w-auto">
                {changingPassword ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                Salva Password
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
